import React from 'react';
import { useQuery } from 'react-query';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { lighten } from '@mui/material/styles';

import Client from 'components/Shared/Client';
import GeswebLink from 'components/Shared/GeswebLink';
import { fetchCommandeGestion } from 'utils/ts-queries';
import { ApiCommandeGestionLigne } from 'types/apiReturns';

type Props = {
    commandeGestionCle: string;
};

export default function InfosCommande({ commandeGestionCle }: Props) {

    const { isLoading, isError, data } = useQuery({
        queryKey: ['commandeGestion', commandeGestionCle],
        queryFn: () => fetchCommandeGestion(commandeGestionCle),
        enabled: Boolean(commandeGestionCle),
        keepPreviousData: true
    });

    if (isLoading) {
        return <Box><CircularProgress size={24} /></Box>;
    }

    if (isError || !data) {
        return <Typography>Aucune donnée disponible pour la commande.</Typography>;
    }

    const lignes = data.lignes ?? [];

    const totalCommandee = lignes.reduce((s, l) => s + l.quantiteCommandee, 0);
    const totalLivree = lignes.reduce((s, l) => s + l.quantiteLivree, 0);
    const totalEnCours = lignes.reduce((s, l) => s + l.quantiteEnCoursDossier, 0);
    const totalRetournable = lignes.reduce((s, l) => s + l.quantiteRetournable, 0);

    const dateCommande = data.dateCreation
        ? new Date(data.dateCreation).toLocaleDateString('fr-FR')
        : null;

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="baseline" flexWrap="wrap" mb={2}>
                <Typography variant="h6">Commande {data.numeroPieceOrigine || data.numeroPiece}</Typography>
                <Chip label={`${data.lignes.length} ligne${data.lignes.length > 1 ? 's' : ''}`} color="primary" size="small" />
                <Typography variant="body2" component="span" sx={{ flexGrow: 1 }}>
                    {dateCommande && <>du {dateCommande} &nbsp;·&nbsp; </>}
                    {data.referenceCommande && <>Réf : {data.referenceCommande} &nbsp;·&nbsp; </>}
                    {data.client && <>Client <Client client={data.client} showMarcheBadge showPrioritaireBadge /></>}
                </Typography>
                {data.cle && (
                    <span onClick={e => e.stopPropagation()} title="Ouvrir la commande dans GesWeb">
                        <GeswebLink mode="button" label={null} structure="commande" cle={data.cle} />
                    </span>
                )}
            </Stack>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 10 * 33 + 37 + 37 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow sx={{ '& th': { backgroundColor: 'grey.100' } }}>
                            <TableCell>N° ligne</TableCell>
                            <TableCell>Code article</TableCell>
                            <TableCell>Désignation article</TableCell>
                            <TableCell>Fournisseur</TableCell>
                            <TableCell>Pièce(s) livraison</TableCell>
                            <TableCell align="center">Qté commandée</TableCell>
                            <TableCell align="center">Qté livrée</TableCell>
                            <TableCell align="center">Qté dossier en cours</TableCell>
                            <TableCell align="center">Qté retournable</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lignes.map((ligne) => (
                            <LigneRow key={ligne.numero} ligne={ligne} />
                        ))}
                        <TableRow
                            sx={{
                                '& td': {
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: theme => lighten(theme.palette.secondary.main, 0.85),
                                    zIndex: 2,
                                },
                            }}
                        >
                            <TableCell colSpan={5} sx={{ fontWeight: 600 }}>TOTAL</TableCell>
                            <TableCell align="center" sx={{ color: 'secondary.main', fontWeight: 600 }}>{totalCommandee}</TableCell>
                            <TableCell align="center" sx={{ color: 'secondary.main', fontWeight: 600 }}>{totalLivree}</TableCell>
                            <TableCell align="center" sx={{ color: 'secondary.main', fontWeight: 600 }}>{totalEnCours}</TableCell>
                            <TableCell align="center" sx={{ color: 'secondary.main', fontWeight: 600 }}>{totalRetournable}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

function LigneRow({ ligne }: { ligne: ApiCommandeGestionLigne }) {
    const greyed = ligne.quantiteRetournable === 0;

    return (
        <TableRow
            sx={greyed ? {
                backgroundColor: 'grey.50',
                '& td': { color: 'text.disabled' },
            } : undefined}
        >
            <TableCell>{ligne.numero}</TableCell>
            <TableCell>{ligne.codeArticle}</TableCell>
            <TableCell>{ligne.designationArticle}</TableCell>
            <TableCell>{ligne.libelleFournisseur || ligne.codeFournisseur || ''}</TableCell>
            {ligne.piecesLivraison && ligne.piecesLivraison.length > 0
                ? (
                    <TableCell>
                        {ligne.piecesLivraison.map((p) => <div key={p}>{p}</div>)}
                    </TableCell>
                )
                : (
                    <Tooltip title="Article pas encore livré au client">
                        <TableCell sx={{ color: 'rgba(0, 0, 0, 0.4)' }}>--</TableCell>
                    </Tooltip>
                )}
            <TableCell align="center">{ligne.quantiteCommandee}</TableCell>
            <TableCell align="center">{ligne.quantiteLivree}</TableCell>
            <TableCell align="center">{ligne.quantiteEnCoursDossier}</TableCell>
            <TableCell align="center">{ligne.quantiteRetournable}</TableCell>
        </TableRow>
    );
}
